import { motion } from "framer-motion";

const STAGES = [
  {
    n: "01",
    name: "Solve",
    desc: "Model produces its initial answer and a prior confidence.",
    color: "#FF4D00",
  },
  {
    n: "02",
    name: "Self-Eval",
    desc: "Model critiques its own answer — identifies assumptions, flags gaps.",
    color: "#FF4D00",
  },
  {
    n: "03",
    name: "Adversarial Probe",
    desc: "Benchmark injects a principled counter-argument for the opposite conclusion.",
    color: "#00F0FF",
  },
  {
    n: "04",
    name: "Revision",
    desc: "Model must commit: defend, abstain, or overturn. The collapse is measured here.",
    color: "#00F0FF",
  },
];

export default function HowItWorks() {
  return (
    <section
      id="how"
      data-testid="how-section"
      className="relative py-32 px-6 lg:px-10 border-t border-white/5"
    >
      <div className="absolute inset-0 tech-grid opacity-25 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <div className="chapter-label mb-6">04 · How It Works</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-neutral-100 max-w-4xl leading-tight">
            The V5 protocol is adversarial by design.
          </h2>
          <p className="mt-5 text-neutral-400 max-w-3xl leading-relaxed">
            Four stages, each one harder than the last. We do not measure
            whether a model <span className="italic text-neutral-200">can answer</span> —
            we measure whether it <span className="italic text-[#FF4D00]">knows when it shouldn't</span>.
          </p>
        </motion.div>

        {/* Stages — horizontal timeline */}
        <div className="relative mb-24">
          <div className="hidden lg:block absolute top-8 left-0 right-0 h-px">
            <div className="h-full bg-gradient-to-r from-[#FF4D00] via-[#FF4D00] to-[#00F0FF] opacity-50" />
          </div>

          <div className="grid lg:grid-cols-4 gap-6">
            {STAGES.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                data-testid={`protocol-stage-${s.n}`}
                className="relative"
              >
                <div
                  className="relative w-16 h-16 border-2 flex items-center justify-center bg-[#0a0a0a] z-10"
                  style={{ borderColor: s.color }}
                >
                  <span
                    className="font-mono text-xs tracking-widest"
                    style={{ color: s.color }}
                  >
                    {s.n}
                  </span>
                  <div
                    className="absolute -inset-1 animate-pulse opacity-30 blur-sm"
                    style={{ background: s.color }}
                  />
                </div>
                <div className="mt-6 border-l border-white/10 pl-5 py-1">
                  <div
                    className="font-serif-display text-2xl text-neutral-100"
                    style={{ textShadow: `0 0 16px ${s.color}33` }}
                  >
                    {s.name}
                  </div>
                  <div className="mt-2 text-sm text-neutral-500 leading-relaxed">
                    {s.desc}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Deep_probe vs Extended */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="grid lg:grid-cols-2 gap-6"
        >
          {/* STANDARD Mode */}
          <div
            data-testid="mode-standard"
            className="border border-[#00F0FF]/40 p-8 bg-[#00F0FF]/5 relative overflow-hidden flex flex-col justify-between"
          >
            <div className="absolute top-0 left-0 w-64 h-64 bg-[#00F0FF]/10 blur-3xl rounded-full pointer-events-none" />
            <div className="relative">
              <div className="font-mono text-[11px] text-[#00F0FF] tracking-[0.3em]">
                MODE · STANDARD
              </div>
              <div className="font-serif-display text-5xl text-neutral-100 mt-3">
                200
              </div>
              <div className="mt-1 text-neutral-500 text-sm">
                fast run · multi-model
              </div>
              <ul className="mt-6 space-y-3 text-sm text-neutral-300">
                <li className="flex gap-3">
                  <span className="text-[#00F0FF]">▸</span>
                  Quick assessment of a model's metacognitive baseline.
                </li>
                <li className="flex gap-3">
                  <span className="text-[#00F0FF]">▸</span>
                  Highly curated subset of the most discriminative items.
                </li>
                <li className="flex gap-3">
                  <span className="text-[#00F0FF]">▸</span>
                  Ideal for rapid continuous integration and testing.
                </li>
              </ul>
            </div>
          </div>

          {/* EXTENDED Mode */}
          <div
            data-testid="mode-extended"
            className="border border-[#00F0FF]/40 p-8 bg-[#00F0FF]/5 relative overflow-hidden flex flex-col justify-between"
          >
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#00F0FF]/10 blur-3xl rounded-full pointer-events-none" />
            <div className="relative">
              <div className="font-mono text-[11px] text-[#00F0FF] tracking-[0.3em]">
                MODE · EXTENDED
              </div>
              <div className="font-serif-display text-5xl text-neutral-100 mt-3">
                350
              </div>
              <div className="mt-1 text-neutral-500 text-sm">
                curated · multi-model comparison
              </div>
              <ul className="mt-6 space-y-3 text-sm text-neutral-300">
                <li className="flex gap-3">
                  <span className="text-[#00F0FF]">▸</span>
                  Fast turnaround. Good for rapid tournament-style runs.
                </li>
                <li className="flex gap-3">
                  <span className="text-[#00F0FF]">▸</span>
                  Known to overstate ECI — see the Scale Validity Gap.
                </li>
                <li className="flex gap-3">
                  <span className="text-[#00F0FF]">▸</span>
                  Useful as a directional signal, not a headline.
                </li>
              </ul>
            </div>
          </div>

          {/* DEEP_PROBE Mode */}
          <div
            data-testid="mode-deep-probe"
            className="border border-[#FF4D00]/40 p-8 bg-[#FF4D00]/5 relative overflow-hidden lg:col-span-2 flex flex-col justify-between mt-2 lg:w-[90%] mx-auto"
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#FF4D00]/10 blur-3xl rounded-full pointer-events-none" />
            <div className="relative lg:text-center">
              <div className="font-mono text-[11px] text-[#FF4D00] tracking-[0.3em]">
                MODE · DEEP_PROBE
              </div>
              <div className="font-serif-display text-5xl text-neutral-100 mt-3">
                1,459
              </div>
              <div className="mt-1 text-neutral-500 text-sm">
                items · full scale · headline ECI
              </div>
              <div className="mt-8 flex flex-col md:flex-row gap-8 justify-center text-sm text-neutral-300">
                <div className="flex-1 flex flex-col items-center text-center gap-2">
                  <span className="text-[#FF4D00]">▸</span>
                  <span>Full anti-contamination coverage across all 4 solvency classes.</span>
                </div>
                <div className="flex-1 flex flex-col items-center text-center gap-2">
                  <span className="text-[#FF4D00]">▸</span>
                  <span>Bootstrap CIs, permutation tests, <span className="whitespace-nowrap">d-prime</span> / bias.</span>
                </div>
                <div className="flex-1 flex flex-col items-center text-center gap-2">
                  <span className="text-[#FF4D00]">▸</span>
                  <span>Required for any publishable headline number.</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Scale gap reminder */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="mt-12 text-center"
        >
          <div className="font-serif-display italic text-xl lg:text-2xl text-neutral-400 max-w-3xl mx-auto">
            “Every frontier model looks more calibrated on 200 items than on
            <span className="text-[#FF4D00]"> 1,459</span>. That gap is the benchmark.”
          </div>
        </motion.div>
      </div>
    </section>
  );
}
